import { FC, useCallback, useEffect, useState } from 'react';

import {
  DashboardRole,
  getDashboardSalesDetail,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  DashboardSalesDetailResponse,
  SalesDetailOrder,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';
import { LowerRole } from '@shared/types';
import { formatPrice } from '@shared/utils/formatPrice';

import { fmtDate, fmtPercent, fmtPeriod } from '../utils/dashboard.utils';
import {
  CenterLoading,
  EmptyBox,
  ErrorBox,
  StatCard,
  TD_CLASS,
  TH_CLASS,
} from './DashboardShared';

const paymentStatusKo: Record<string, string> = {
  Paid: '결제완료',
  Confirmed: '확인됨',
  Canceled: '취소됨',
  Failed: '실패',
  Pending: '대기중',
};

const paymentMethodKo: Record<string, string> = {
  card: '카드',
  vbank: '가상계좌',
  trans: '계좌이체',
  phone: '휴대폰',
  cash: '현금',
};

export const DashboardSalesDetailTab: FC<{
  from: string;
  to: string;
  role?: DashboardRole;
}> = ({ from, to, role }) => {
  const [data, setData] = useState<DashboardSalesDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getDashboardSalesDetail(from, to, role));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '데이터를 불러올 수 없습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <CenterLoading />;
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return <EmptyBox />;

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <section className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-gray-800">
            매출·수수료 상세
            <span className="ml-2 text-xs font-normal text-gray-400">
              결제일 기준 ({fmtPeriod(from, to)})
            </span>
          </h2>
          <p className="mt-1 text-xs text-gray-400">
            주문별 매출과 수수료 내역입니다. 수수료율{' '}
            {fmtPercent(data.constants.serviceFeeRate)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label="총 매출"
            value={`${formatPrice(data.totalGrossSales)}원`}
            sub={`${data.orderCount}건`}
            accent="indigo"
          />
          <StatCard
            label="수수료"
            value={`${formatPrice(data.totalServiceFee)}원`}
            accent="red"
          />
          <StatCard
            label="매출 - 수수료"
            value={`${formatPrice(data.totalBalanceAfterFee)}원`}
            accent="emerald"
          />
          <StatCard label="주문 수" value={`${data.orderCount}건`} />
        </div>
      </section>

      {/* 주문 테이블 */}
      <section className="rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={TH_CLASS}>#</th>
                <th className={TH_CLASS}>결제일</th>
                {role !== LowerRole.BugoAgencyAdmin && (
                  <th className={TH_CLASS}>구매자</th>
                )}
                <th className={TH_CLASS}>고인명</th>
                <th className={TH_CLASS}>발인일</th>
                <th className={TH_CLASS}>행사등록자</th>
                <th className={TH_CLASS}>상태</th>
                <th className={`${TH_CLASS} text-right`}>총 매출</th>
                <th className={`${TH_CLASS} text-right`}>도매가 합계</th>
                <th className={`${TH_CLASS} text-right`}>수수료</th>
                <th className={`${TH_CLASS} text-right`}>잔액</th>
                <th className={`${TH_CLASS} text-right`}>수익금</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {data.orders.length === 0 ? (
                <tr>
                  <td
                    colSpan={role === LowerRole.BugoAgencyAdmin ? 11 : 12}
                    className="px-3 py-8 text-center text-sm text-gray-400"
                  >
                    해당 기간 주문 내역이 없습니다.
                  </td>
                </tr>
              ) : (
                data.orders.map((order, i) => (
                  <OrderRow
                    key={order.orderId}
                    order={order}
                    index={i}
                    expanded={expandedId === order.orderId}
                    onToggle={() =>
                      setExpandedId(expandedId === order.orderId ? null : order.orderId)
                    }
                    role={role}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

const OrderRow: FC<{
  order: SalesDetailOrder;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  role?: DashboardRole;
}> = ({ order, index, expanded, onToggle, role }) => (
  <>
    <tr onClick={onToggle} className="cursor-pointer transition-colors hover:bg-gray-50">
      <td className={`${TD_CLASS} text-gray-400`}>{index + 1}</td>
      <td className={TD_CLASS}>{fmtDate(order.purchasedAt)}</td>
      {role !== LowerRole.BugoAgencyAdmin && (
        <td className={TD_CLASS}>
          {order.receiverName}
          <span className="ml-1 text-xs text-gray-400">{order.receiverPhoneNumber}</span>
        </td>
      )}
      <td className={TD_CLASS}>{order.deceasedName}</td>
      <td className={TD_CLASS}>{fmtDate(order.coffinOutDate)}</td>
      <td className={TD_CLASS}>
        <div className="leading-tight">
          <span>{order.feventUserName}</span>
          {order.feventUserPhone && (
            <span className="ml-1 text-xs text-gray-400">{order.feventUserPhone}</span>
          )}
        </div>
      </td>
      <td className={TD_CLASS}>
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
            order.paymentStatus === 'Paid'
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-blue-100 text-blue-700'
          }`}
        >
          {paymentStatusKo[order.paymentStatus] ?? order.paymentStatus}
        </span>
      </td>
      <td className={`${TD_CLASS} text-right font-medium`}>
        {formatPrice(order.grossSales)}원
      </td>
      <td className={`${TD_CLASS} text-right font-medium text-orange-600`}>
        {formatPrice(order.totalWholesale)}원
      </td>
      <td className={`${TD_CLASS} text-right text-red-500`}>
        {formatPrice(order.serviceFee)}원
      </td>
      <td className={`${TD_CLASS} text-right font-medium text-emerald-700`}>
        {formatPrice(order.balanceAfterFee)}원
      </td>
      <td className={`${TD_CLASS} textㄷ-indigo-700 text-right font-bold`}>
        {formatPrice(order.balanceAfterFee - order.totalWholesale)}원
      </td>
    </tr>
    {expanded && (
      <tr>
        <td
          colSpan={role === LowerRole.BugoAgencyAdmin ? 11 : 12}
          className="bg-gray-50 px-6 py-4"
        >
          <div className="mb-2 flex flex-wrap gap-4 text-xs text-gray-500">
            <span>
              결제수단: {paymentMethodKo[order.paymentMethod] ?? order.paymentMethod}
            </span>
            <span>결제금: {formatPrice(order.paymentByCash)}원</span>
            {order.cancelledTotalPrice > 0 && (
              <span className="text-red-500">
                취소금: {formatPrice(order.cancelledTotalPrice)}원
              </span>
            )}
            <span>면세 매출: {formatPrice(order.netTaxFree)}원</span>
            <span>과세 매출: {formatPrice(order.netTaxable)}원</span>
            <span className="text-emerald-600">
              면세 잔액: {formatPrice(order.taxFreeBalance ?? 0)}원
            </span>
            <span className="text-emerald-600">
              과세 잔액: {formatPrice(order.taxableBalance ?? 0)}원
            </span>
            <span className="font-semibold text-orange-600">
              도매가 합계: {formatPrice(order.totalWholesale)}원
            </span>
          </div>
          <p className="mb-2 text-[10px] text-gray-400">
            도매가 합계는 실제 이행 상태 기반 합산이며, 상품별 도매가(단가)는 주문 시점
            기록입니다.
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500">
                <th className="py-1 text-left font-medium">상품명</th>
                <th className="py-1 text-left font-medium">유형</th>
                <th className="py-1 text-right font-medium">수량</th>
                <th className="py-1 text-right font-medium">소비자가</th>
                <th className="py-1 text-right font-medium">도매가</th>
                <th className="py-1 text-right font-medium">과세</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {order.items.map((item, j) => (
                <tr key={j}>
                  <td className="py-1">{item.giftItemName}</td>
                  <td className="py-1">
                    <span
                      className={`rounded px-1 py-0.5 text-[10px] font-medium ${
                        item.type === 'Coupon'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {item.type === 'Coupon' ? '쿠폰' : '배송'}
                    </span>
                  </td>
                  <td className="py-1 text-right">{item.quantity}</td>
                  <td className="py-1 text-right">{formatPrice(item.priceRetail)}원</td>
                  <td className="py-1 text-right text-orange-600">
                    {formatPrice(item.priceWhole)}원
                  </td>
                  <td className="py-1 text-right">{item.isTaxable ? '과세' : '면세'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    )}
  </>
);
